import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Close,
  DeleteOutline,
  Edit,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChatbotModal from "../../components/ChatbotModal";
import MessageBubblePreview from "../../components/MessageBubblePreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { buildWhatsappTabs, getWhatsappTabParams } from "../../utils/connectionsTabs";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";
import {
  buildMessageVariables,
  fetchContactCustomFieldNames,
  fetchWasheetMessageVariables,
} from "../../utils/messageVariables";

const NO_RECURRENCE_UNIT = "__no_recurrence_unit__";

const useStyles = makeStyles(theme => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
      "& .MuiButton-root": {
        minHeight: 42,
      },
      "& .MuiButton-label": {
        whiteSpace: "normal",
        lineHeight: 1.15,
      },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    },
  },
  formBox: {
    padding: theme.spacing(1.5),
    margin: 20,
    marginBottom: 0,
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fafafa",
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      marginBottom: 0,
      padding: theme.spacing(1.25),
    },
  },
  textField: {
    width: "100%",
  },
  formControl: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  block: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fff",
  },
  blockHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  blockActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  blockGrid: {
    display: "grid",
    gridTemplateColumns: "330px minmax(0, 1fr) 90px",
    gap: theme.spacing(1),
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "290px minmax(0, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageDelayField: {
    [theme.breakpoints.down("sm")]: {
      gridColumn: "2",
    },
    [theme.breakpoints.down("xs")]: {
      gridColumn: "auto",
    },
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  fileName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },
  helper: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  contactCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  messagePreview: {
    maxWidth: 360,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
  censoredPhoto: {
    filter: "blur(6px)",
  },
  variablesBox: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fff",
  },
  variablesLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  variablesChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
      alignItems: "stretch",
      "& .MuiButton-root": {
        flex: "1 1 auto",
      },
    },
  },
  bulkActionsText: {
    fontWeight: 600,
    marginRight: "auto",
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "auto auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileContactName: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  mobileContactNumber: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  mobileCardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  mobileValue: {
    minWidth: 0,
    textAlign: "right",
    wordBreak: "break-word",
  },
  mobileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
}));

const initialForm = {
  id: null,
  contact: null,
  title: "",
  actionType: "message",
  date: "",
  time: "",
  recurrenceValue: "",
  recurrenceUnit: "",
  chatbotSequenceId: "",
};

const acceptedMedia =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar,.webp";
const NO_SEQUENCE = "no-sequence";

const createBlock = () => ({
  text: "",
  delaySeconds: 0,
  file: null,
  media: null,
});

const pad = value => String(value).padStart(2, "0");

const toLocalParts = value => {
  if (!value) return { date: "", time: "" };
  const date = new Date(value);
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const getDefaultDateTime = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() + 5);
  return toLocalParts(date);
};

const getReminderMessage = reminder => {
  if (reminder.actionType === "sequence") {
    return reminder.chatbotSequence?.name || i18n.t("ticketRemindersModal.actionTypes.sequence");
  }

  return (reminder.blocks || [])
    .map(block => block.text || block.media?.originalName || "")
    .filter(Boolean)
    .join(" | ");
};

const formatSequenceOption = sequence =>
  `ID: ${sequence.id} - ${sequence.name}`;

const Reminders = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const isMounted = useRef(true);
  const [reminders, setReminders] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [connections, setConnections] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...initialForm, ...getDefaultDateTime() });
  const [blocks, setBlocks] = useState([createBlock()]);
  const [deletingReminder, setDeletingReminder] = useState(null);
  const [selectedReminderIds, setSelectedReminderIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [washeetVariables, setWasheetVariables] = useState([]);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const messageVariables = useMemo(
    () => buildMessageVariables(customFieldNames, washeetVariables),
    [customFieldNames, washeetVariables]
  );
  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections, orphanGroups),
    [connections, orphanGroups]
  );
  const selectedWhatsappTab = whatsappTabs.find(
    tab => String(tab.value) === String(selectedConnectionId)
  );

  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const [
          { data },
          { data: orphanData },
          nextCustomFieldNames,
          nextWasheetVariables,
        ] = await Promise.all([
          api.get("/connections/"),
          api.get("/connections/orphan-groups"),
          fetchContactCustomFieldNames(),
          fetchWasheetMessageVariables(),
        ]);
        const loadedConnections = data || [];
        const loadedOrphans = orphanData || [];
        const tabs = buildWhatsappTabs(loadedConnections, loadedOrphans);
        setConnections(loadedConnections);
        setOrphanGroups(loadedOrphans);
        setCustomFieldNames(nextCustomFieldNames);
        setWasheetVariables(nextWasheetVariables);
        setSelectedConnectionId(prevState => prevState || tabs[0]?.value || "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchConnections();
  }, []);

  const fetchData = async () => {
    if (!selectedConnectionId) return;

    setLoading(true);
    try {
      const tabParams = getWhatsappTabParams(selectedConnectionId);
      const [{ data: reminderData }, { data: sequenceData }] = await Promise.all([
        api.get("/reminders", { params: tabParams }),
        api.get("/chatbot/sequences", {
          params: {
            all: true,
            pageNumber: 1,
            searchParam: "",
            ...tabParams,
          },
        }),
      ]);

      if (isMounted.current) {
        setReminders(reminderData.reminders || []);
        setSequences(sequenceData.sequences || []);
        setSelectedReminderIds([]);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedConnectionId]);

  useEffect(() => {
    const reminderIds = new Set(reminders.map(reminder => String(reminder.id)));
    setSelectedReminderIds(prevState =>
      prevState.filter(reminderId => reminderIds.has(String(reminderId)))
    );
  }, [reminders]);

  useEffect(() => {
    const normalizedSearch = contactSearch.trim();
    if (normalizedSearch.length < 2 && !showForm) return;

    const delay = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: normalizedSearch, pageNumber: 1 },
        });
        setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [contactSearch, showForm]);

  const reminderContactOptions = useMemo(() => {
    const options = [...contactOptions];
    if (
      form.contact?.id &&
      !options.some(option => String(option.id) === String(form.contact.id))
    ) {
      options.unshift(form.contact);
    }
    return options;
  }, [contactOptions, form.contact]);

  const renderContactOption = option => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Avatar
        src={privacyCensorSettings.contactPhotos ? undefined : option?.profilePicUrl}
        className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
        style={{ width: 28, height: 28 }}
      >
        {privacyCensorSettings.contactNames ? undefined : option?.name?.[0]}
      </Avatar>
      <div>
        <Typography
          variant="body2"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames
            ? "Contacto privado"
            : option?.name || "Sin nombre"}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames
            ? "Telefono privado"
            : option?.number || ""}
        </Typography>
      </div>
    </div>
  );

  const resetForm = () => {
    setContactSearch("");
    setForm({ ...initialForm, ...getDefaultDateTime() });
    setBlocks([createBlock()]);
    setShowForm(false);
  };

  const handleEdit = reminder => {
    setContactSearch("");
    setForm({
      id: reminder.id,
      contact: reminder.contact || null,
      title: reminder.title || "",
      actionType: reminder.actionType || "message",
      ...toLocalParts(reminder.scheduledAt || reminder.nextRunAt),
      recurrenceValue: reminder.recurrenceValue || "",
      recurrenceUnit: reminder.recurrenceUnit || "",
      chatbotSequenceId: reminder.chatbotSequenceId || "",
    });
    setBlocks(
      reminder.blocks?.length
        ? reminder.blocks.map(block => ({
            text: block.text || "",
            delaySeconds: Number(block.delaySeconds || 0),
            file: null,
            media: block.media || null,
          }))
        : [createBlock()]
    );
    setShowForm(true);
  };

  const buildFormData = () => {
    const scheduledAt = new Date(`${form.date}T${form.time}:00`);
    const formData = new FormData();
    const normalizedBlocks = blocks.map(block => ({
      text: block.text || "",
      delaySeconds: Number(block.delaySeconds || 0),
      media: block.file ? undefined : block.media,
    }));

    formData.append("title", form.title);
    formData.append("actionType", form.actionType);
    formData.append("scheduledAt", scheduledAt.toISOString());
    formData.append("recurrenceValue", form.recurrenceValue || "");
    formData.append("recurrenceUnit", form.recurrenceUnit || "");
    formData.append("chatbotSequenceId", form.chatbotSequenceId || "");
    formData.append("connectionId", selectedConnectionId || "");
    formData.append("blocks", JSON.stringify(normalizedBlocks));

    blocks.forEach((block, index) => {
      if (block.file) {
        formData.append(`blockFile_${index}`, block.file);
      }
    });

    return formData;
  };

  const validateForm = () => {
    if (!form.id && !form.contact?.id) {
      toast.error(i18n.t("remindersPage.validation.contact"));
      return false;
    }
    if (!form.date || !form.time) {
      toast.error(i18n.t("ticketRemindersModal.validation.datetime"));
      return false;
    }
    if (form.actionType === "sequence" && !form.chatbotSequenceId) {
      toast.error(i18n.t("ticketRemindersModal.validation.sequence"));
      return false;
    }
    if (
      form.actionType === "message" &&
      !blocks.some(block => block.text.trim() || block.file || block.media)
    ) {
      toast.error(i18n.t("ticketRemindersModal.validation.message"));
      return false;
    }
    return true;
  };

  const updateBlock = (index, patch) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) =>
        blockIndex === index ? { ...block, ...patch } : block
      )
    );
  };

  const moveBlock = (index, direction) => {
    setBlocks(prevBlocks => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prevBlocks.length) return prevBlocks;

      const nextBlocks = [...prevBlocks];
      const current = nextBlocks[index];
      nextBlocks[index] = nextBlocks[nextIndex];
      nextBlocks[nextIndex] = current;
      return nextBlocks;
    });
  };

  const removeBlock = index => {
    setBlocks(prevBlocks =>
      prevBlocks.length === 1
        ? [createBlock()]
        : prevBlocks.filter((_, blockIndex) => blockIndex !== index)
    );
  };

  const upsertSequence = sequence => {
    if (!sequence?.id) return;
    setSequences(prevSequences => {
      const exists = prevSequences.some(item => String(item.id) === String(sequence.id));
      return exists
        ? prevSequences.map(item => (String(item.id) === String(sequence.id) ? sequence : item))
        : [...prevSequences, sequence];
    });
  };

  const openCreateSequence = () => {
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = sequenceId => {
    if (!sequenceId || sequenceId === NO_SEQUENCE) return;
    setSequenceModalId(sequenceId);
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    upsertSequence(savedSequence);
    if (savedSequenceId) {
      setForm(prevForm => ({
        ...prevForm,
        chatbotSequenceId: String(savedSequenceId),
      }));
    }
    setSequenceModalOpen(false);
    setSequenceModalId(null);
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const formData = buildFormData();
      const config = { headers: { "Content-Type": "multipart/form-data" } };

      if (form.id) {
        await api.put(`/reminders/${form.id}`, formData, config);
      } else {
        await api.post(`/contacts/${form.contact.id}/reminders`, formData, config);
      }

      toast.success(i18n.t("ticketRemindersModal.toasts.saved"));
      resetForm();
      fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async reminderId => {
    try {
      await api.delete(`/reminders/${reminderId}`);
      toast.success(i18n.t("ticketRemindersModal.toasts.deleted"));
      setDeletingReminder(null);
      fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  const allRemindersSelected =
    reminders.length > 0 &&
    reminders.every(reminder => selectedReminderIds.includes(String(reminder.id)));
  const selectedReminderCount = selectedReminderIds.length;

  const toggleReminderSelection = reminderId => {
    const normalizedId = String(reminderId);
    setSelectedReminderIds(prevState =>
      prevState.includes(normalizedId)
        ? prevState.filter(id => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllRemindersSelection = () => {
    if (allRemindersSelected) {
      setSelectedReminderIds([]);
      return;
    }

    setSelectedReminderIds(reminders.map(reminder => String(reminder.id)));
  };

  const handleDeleteSelectedReminders = async () => {
    try {
      await Promise.all(
        selectedReminderIds.map(reminderId => api.delete(`/reminders/${reminderId}`))
      );
      toast.success(i18n.t("ticketRemindersModal.toasts.deleted"));
      setSelectedReminderIds([]);
      fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleteOpen(false);
    }
  };

  const addVariableToBlock = (index, variable) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) => {
        if (blockIndex !== index) return block;
        const separator = block.text?.trim() ? " " : "";
        return { ...block, text: `${block.text || ""}${separator}${variable}` };
      })
    );
  };

  const renderReminderActions = reminder => (
    <>
      <IconButton size="small" onClick={() => handleEdit(reminder)}>
        <Edit fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => setDeletingReminder(reminder)}>
        <DeleteOutline fontSize="small" />
      </IconButton>
    </>
  );

  const renderMobileReminderCard = reminder => (
    <div className={classes.mobileCard} key={reminder.id}>
      <div className={classes.mobileCardHeader}>
        <Checkbox
          size="small"
          color="primary"
          checked={selectedReminderIds.includes(String(reminder.id))}
          onChange={() => toggleReminderSelection(reminder.id)}
          inputProps={{
            "aria-label": `Seleccionar ${
              reminder.title || reminder.contact?.name || "recordatorio"
            }`,
          }}
        />
        <Avatar
          src={
            privacyCensorSettings.contactPhotos
              ? undefined
              : reminder.contact?.profilePicUrl
          }
          className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
        />
        <div>
          <Typography
            className={
              privacyCensorSettings.contactNames
                ? classes.censoredText
                : classes.mobileContactName
            }
          >
            {privacyCensorSettings.contactNames
              ? "Contacto privado"
              : reminder.contact?.name || reminder.title || "Recordatorio"}
          </Typography>
          <Typography
            className={
              privacyCensorSettings.contactNames
                ? classes.censoredText
                : classes.mobileContactNumber
            }
          >
            {privacyCensorSettings.contactNames
              ? "Telefono privado"
              : reminder.contact?.number || "-"}
          </Typography>
        </div>
        <div className={classes.mobileActions}>
          {renderReminderActions(reminder)}
        </div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("remindersPage.table.connection")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {reminder.connection?.name || "-"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("remindersPage.table.date")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {reminder.nextRunAt ? new Date(reminder.nextRunAt).toLocaleString() : "-"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("remindersPage.table.status")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {i18n.t(`ticketRemindersModal.status.${reminder.status}`)}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("remindersPage.table.message")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {getReminderMessage(reminder) || "-"}
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("ticketRemindersModal.confirmationModal.deleteTitle")}
        open={!!deletingReminder}
        onClose={() => setDeletingReminder(null)}
        onConfirm={() => handleDelete(deletingReminder.id)}
      >
        {i18n.t("ticketRemindersModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={`Eliminar ${selectedReminderCount} recordatorio${
          selectedReminderCount === 1 ? "" : "s"
        }?`}
        open={bulkDeleteOpen}
        onClose={setBulkDeleteOpen}
        onConfirm={handleDeleteSelectedReminders}
      >
        {i18n.t("ticketRemindersModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={selectedConnectionId}
        initialSequenceName="Recordatorio"
      />

      <MainHeader>
        <div className={classes.headerContent}>
          <Title>{i18n.t("remindersPage.title")}</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <Button
              color="primary"
              variant="contained"
              startIcon={showForm ? <Close /> : <Add />}
              disabled={!showForm && (!selectedConnectionId || selectedWhatsappTab?.orphan)}
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
            >
              {showForm
                ? i18n.t("ticketRemindersModal.buttons.cancel")
                : i18n.t("remindersPage.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>

      {showForm && (
        <Paper className={classes.formBox} elevation={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={form.contact}
                disabled={Boolean(form.id)}
                options={reminderContactOptions}
                getOptionLabel={option =>
                  option ? `${option.name || "Sin nombre"} - ${option.number || ""}` : ""
                }
                getOptionSelected={(option, value) => option.id === value.id}
                onInputChange={(_, value) => setContactSearch(value)}
                onChange={(_, value) => setForm({ ...form, contact: value })}
                renderOption={renderContactOption}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={i18n.t("remindersPage.form.contact")}
                    variant="outlined"
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={i18n.t("ticketRemindersModal.form.title")}
                value={form.title}
                onChange={event => setForm({ ...form, title: event.target.value })}
                variant="outlined"
                margin="dense"
                className={classes.textField}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>{i18n.t("ticketRemindersModal.form.actionType")}</InputLabel>
                <Select
                  value={form.actionType}
                  label={i18n.t("ticketRemindersModal.form.actionType")}
                  onChange={event => setForm({ ...form, actionType: event.target.value })}
                >
                  <MenuItem value="message">
                    {i18n.t("ticketRemindersModal.actionTypes.message")}
                  </MenuItem>
                  <MenuItem value="sequence">
                    {i18n.t("ticketRemindersModal.actionTypes.sequence")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label={i18n.t("ticketRemindersModal.form.date")}
                type="date"
                value={form.date}
                onChange={event => setForm({ ...form, date: event.target.value })}
                variant="outlined"
                margin="dense"
                className={classes.textField}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label={i18n.t("ticketRemindersModal.form.time")}
                type="time"
                value={form.time}
                onChange={event => setForm({ ...form, time: event.target.value })}
                variant="outlined"
                margin="dense"
                className={classes.textField}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label={i18n.t("ticketRemindersModal.form.recurrenceValue")}
                type="number"
                value={form.recurrenceValue}
                onChange={event =>
                  setForm({ ...form, recurrenceValue: event.target.value })
                }
                variant="outlined"
                margin="dense"
                className={classes.textField}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>{i18n.t("ticketRemindersModal.form.recurrenceUnit")}</InputLabel>
                <Select
                  value={form.recurrenceUnit || NO_RECURRENCE_UNIT}
                  label={i18n.t("ticketRemindersModal.form.recurrenceUnit")}
                  onChange={event =>
                    setForm({
                      ...form,
                      recurrenceUnit:
                        event.target.value === NO_RECURRENCE_UNIT
                          ? ""
                          : event.target.value,
                    })
                  }
                >
                  <MenuItem value={NO_RECURRENCE_UNIT}>{i18n.t("ticketRemindersModal.recurrenceUnits.none")}</MenuItem>
                  <MenuItem value="minutes">{i18n.t("ticketRemindersModal.recurrenceUnits.minutes")}</MenuItem>
                  <MenuItem value="hours">{i18n.t("ticketRemindersModal.recurrenceUnits.hours")}</MenuItem>
                  <MenuItem value="days">{i18n.t("ticketRemindersModal.recurrenceUnits.days")}</MenuItem>
                  <MenuItem value="weeks">{i18n.t("ticketRemindersModal.recurrenceUnits.weeks")}</MenuItem>
                  <MenuItem value="months">{i18n.t("ticketRemindersModal.recurrenceUnits.months")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {form.actionType === "sequence" ? (
                <>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>{i18n.t("ticketRemindersModal.form.sequence")}</InputLabel>
                    <Select
                      value={form.chatbotSequenceId || NO_SEQUENCE}
                      label={i18n.t("ticketRemindersModal.form.sequence")}
                      onChange={event => {
                        if (event.target.value === "__create__") {
                          openCreateSequence();
                          return;
                        }
                        setForm({
                          ...form,
                          chatbotSequenceId:
                            event.target.value === NO_SEQUENCE ? "" : event.target.value,
                        });
                      }}
                    >
                      <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                      <MenuItem value="__create__">Crear secuencia...</MenuItem>
                      {sequences.map(sequence => (
                        <MenuItem key={sequence.id} value={String(sequence.id)}>
                          {formatSequenceOption(sequence)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <div>
                    <Button
                      color="primary"
                      disabled={!form.chatbotSequenceId}
                      onClick={() => openEditSequence(form.chatbotSequenceId)}
                    >
                      Editar secuencia
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {blocks.map((block, index) => (
                    <Paper className={classes.block} elevation={0} key={`reminder-page-block-${index}`}>
                      <div className={classes.blockHeader}>
                        <Typography variant="subtitle2">
                          {i18n.t("ticketRemindersModal.form.message")} {index + 1}
                        </Typography>
                        <div className={classes.blockActions}>
                          <IconButton size="small" onClick={() => moveBlock(index, -1)} disabled={index === 0}>
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1}>
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => removeBlock(index)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                      <div className={classes.blockGrid}>
                        <MessageBubblePreview block={block} />
                        <TextField
                          label={i18n.t("ticketRemindersModal.form.message")}
                          value={block.text}
                          onChange={event => updateBlock(index, { text: event.target.value })}
                          variant="outlined"
                          margin="dense"
                          multiline
                          minRows={5}
                          fullWidth
                        />
                        <TextField
                          className={classes.messageDelayField}
                          label={i18n.t("ticketRemindersModal.form.delay")}
                          type="number"
                          value={block.delaySeconds}
                          onChange={event =>
                            updateBlock(index, {
                              delaySeconds: Math.max(0, Number(event.target.value)),
                            })
                          }
                          variant="outlined"
                          margin="dense"
                          helperText={i18n.t("ticketRemindersModal.recurrenceUnits.seconds")}
                          inputProps={{ min: 0, step: 1 }}
                          fullWidth
                        />
                      </div>
                      <div className={classes.variablesBox}>
                        <Typography variant="caption" className={classes.variablesLabel}>
                          Variables disponibles
                        </Typography>
                        <div className={classes.variablesChips}>
                          {messageVariables.map(variable => (
                            <Chip
                              key={`reminder-page-${index}-${variable.value}`}
                              size="small"
                              label={variable.label}
                              onClick={() => addVariableToBlock(index, variable.value)}
                            />
                          ))}
                        </div>
                      </div>
                      <div className={classes.fileRow}>
                        <input
                          accept={acceptedMedia}
                          id={`reminder-page-file-${index}`}
                          type="file"
                          style={{ display: "none" }}
                          onChange={event =>
                            updateBlock(index, {
                              file: event.target.files?.[0] || null,
                              media: event.target.files?.[0] ? null : block.media,
                            })
                          }
                        />
                        <label htmlFor={`reminder-page-file-${index}`}>
                          <Button variant="outlined" component="span" size="small">
                            {i18n.t("ticketRemindersModal.buttons.file")}
                          </Button>
                        </label>
                        <Typography variant="body2" className={classes.fileName}>
                          {block.file?.name ||
                            block.media?.originalName ||
                            i18n.t("ticketRemindersModal.form.noFile")}
                        </Typography>
                        {(block.file || block.media) && (
                          <Button size="small" onClick={() => updateBlock(index, { file: null, media: null })}>
                            {i18n.t("ticketRemindersModal.buttons.removeFile")}
                          </Button>
                        )}
                      </div>
                      <small className={classes.helper}>
                        Imagenes, stickers .webp, audios, videos y documentos.
                      </small>
                    </Paper>
                  ))}
                  <Button
                    startIcon={<Add />}
                    color="primary"
                    onClick={() => setBlocks(prevBlocks => [...prevBlocks, createBlock()])}
                    style={{ marginTop: 12 }}
                  >
                    {i18n.t("ticketRemindersModal.buttons.addBlock")}
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
          <DialogActions>
            <Button onClick={resetForm} color="secondary" variant="outlined" disabled={saving}>
              {i18n.t("ticketRemindersModal.buttons.cancel")}
            </Button>
            <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : i18n.t("ticketRemindersModal.buttons.save")}
            </Button>
          </DialogActions>
        </Paper>
      )}

      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          value={selectedConnectionId}
          onChange={(event, value) => setSelectedConnectionId(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {whatsappTabs.length ? (
            whatsappTabs.map(whatsapp => (
              <Tab
                key={whatsapp.value}
                value={whatsapp.value}
                label={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
              />
            ))
          ) : (
            <Tab value="" label="Sin conexiones" disabled />
          )}
        </Tabs>
        {reminders.length > 0 && (
          <div className={classes.bulkActions}>
            <Checkbox
              size="small"
              color="primary"
              checked={allRemindersSelected}
              indeterminate={selectedReminderCount > 0 && !allRemindersSelected}
              onChange={toggleAllRemindersSelection}
              inputProps={{ "aria-label": "Seleccionar recordatorios" }}
            />
            <Typography variant="body2" className={classes.bulkActionsText}>
              {selectedReminderCount
                ? `${selectedReminderCount} seleccionado${
                    selectedReminderCount === 1 ? "" : "s"
                  }`
                : "Seleccionar recordatorios"}
            </Typography>
            {selectedReminderCount > 0 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedReminderIds([])}
                >
                  Limpiar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteOutline />}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Eliminar
                </Button>
              </>
            )}
          </div>
        )}
        <div className={classes.mobileCards}>
          {loading ? (
            <CircularProgress size={24} />
          ) : reminders.length ? (
            reminders.map(renderMobileReminderCard)
          ) : (
            <Typography variant="body2">{i18n.t("remindersPage.empty")}</Typography>
          )}
        </div>

        <Table size="small" className={classes.tableWrapper}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{i18n.t("remindersPage.table.contact")}</TableCell>
              <TableCell>{i18n.t("remindersPage.table.connection")}</TableCell>
              <TableCell>{i18n.t("remindersPage.table.date")}</TableCell>
              <TableCell>{i18n.t("remindersPage.table.status")}</TableCell>
              <TableCell>{i18n.t("remindersPage.table.message")}</TableCell>
              <TableCell align="center">{i18n.t("remindersPage.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : reminders.length ? (
              reminders.map(reminder => (
                <TableRow key={reminder.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={selectedReminderIds.includes(String(reminder.id))}
                      onChange={() => toggleReminderSelection(reminder.id)}
                      inputProps={{
                        "aria-label": `Seleccionar ${
                          reminder.title || reminder.contact?.name || "recordatorio"
                        }`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div className={classes.contactCell}>
                      <Avatar
                        src={
                          privacyCensorSettings.contactPhotos
                            ? undefined
                            : reminder.contact?.profilePicUrl
                        }
                        className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
                      />
                      <div>
                        <Typography
                          variant="body2"
                          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
                        >
                          {privacyCensorSettings.contactNames
                            ? "Contacto privado"
                            : reminder.contact?.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
                        >
                          {privacyCensorSettings.contactNames
                            ? "Telefono privado"
                            : reminder.contact?.number}
                        </Typography>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{reminder.connection?.name || "-"}</TableCell>
                  <TableCell>{reminder.nextRunAt ? new Date(reminder.nextRunAt).toLocaleString() : "-"}</TableCell>
                  <TableCell>{i18n.t(`ticketRemindersModal.status.${reminder.status}`)}</TableCell>
                  <TableCell>
                    <Typography
                      className={
                        privacyCensorSettings.conversations
                          ? classes.censoredText
                          : classes.messagePreview
                      }
                      variant="body2"
                    >
                      {privacyCensorSettings.conversations
                        ? "Mensaje privado"
                        : getReminderMessage(reminder)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {renderReminderActions(reminder)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>{i18n.t("remindersPage.empty")}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Reminders;

